/**
 * Which country a provider's category or channel name is tagged with (owner, 2026-10-09:
 * "list US or no country categories before other countries everywhere" — the guide, Movies and
 * Series; 2026-10-20: "Your country", so a viewer outside the US gets their own first). Read from
 * the words of the name, never exact names, so "ar-arabic-movies", "FR| Films", "[DE] Sport",
 * "Uk Dazn", "India Bollywood" and "Australia Kayo" all carry a tag, while "Usa Latin" is the
 * US's and "24-7-drama", "TV Movie", "PPV UFC MMA" and "Live Premier League" carry none.
 *
 * A tag is an ISO 3166 code ("US", "GB", "FR"...), or UNKNOWN_COUNTRY for a language or region
 * that is no one country ("Arabic", "Latin", "Caribbean"): another country for every viewer.
 */

/** A language or region rather than a country: never the viewer's own. */
export const UNKNOWN_COUNTRY = "??";

/** Two-letter starts that are not a foreign country: the US and English, and words providers use. */
const HOME_OR_WORD = new Set(["us", "en", "tv", "hd", "sd", "my", "ny", "la", "dc", "tn", "tx", "ok", "go", "on", "up", "no"]);
/** Two-letter starts whose country is not the code read in capitals ("UK| ..." is Great Britain, "AR" Arabic). */
const TWO_LETTER: Record<string, string> = { uk: "GB", ar: UNKNOWN_COUNTRY, af: UNKNOWN_COUNTRY };
/** Three-letter country and language codes providers lead with ("FRA| ...", "ARA ..."). */
const THREE_LETTER: Record<string, string> = {
  ara: UNKNOWN_COUNTRY, fra: "FR", ger: "DE", deu: "DE", ita: "IT", esp: "ES", spa: "ES", por: "PT", tur: "TR",
  ind: "IN", pak: "PK", mex: "MX", bra: "BR", arg: "AR", aus: "AU", nld: "NL", pol: "PL", rus: "RU", swe: "SE",
  nor: "NO", dnk: "DK", fin: "FI", gre: "GR", kor: "KR", jpn: "JP", chn: "CN", phl: "PH", afr: UNKNOWN_COUNTRY,
  alb: "AL", rom: "RO", hun: "HU",
};
/** A country, nationality or language anywhere in the name, and the country it means. */
const WORDS: Array<[pattern: string, country: string]> = [
  ["arabic", UNKNOWN_COUNTRY], ["arab", UNKNOWN_COUNTRY], ["french", "FR"], ["france", "FR"], ["german", "DE"],
  ["germany", "DE"], ["spanish", "ES"], ["spain", "ES"], ["italian", "IT"], ["italy", "IT"], ["korean", "KR"],
  ["korea", "KR"], ["portuguese", "PT"], ["portugal", "PT"], ["brazil\\w*", "BR"], ["finland", "FI"], ["finnish", "FI"],
  ["india", "IN"], ["indian", "IN"], ["bollywood", "IN"], ["hindi", "IN"], ["tamil", "IN"], ["telugu", "IN"],
  ["punjabi", "IN"], ["urdu", "PK"], ["pakistan\\w*", "PK"], ["bangla\\w*", "BD"], ["latin", UNKNOWN_COUNTRY],
  ["latino", UNKNOWN_COUNTRY], ["netherlands?", "NL"], ["dutch", "NL"], ["turkish", "TR"], ["turkey", "TR"],
  ["polish", "PL"], ["poland", "PL"], ["russian", "RU"], ["russia", "RU"], ["chinese", "CN"], ["china", "CN"],
  ["japanese", "JP"], ["japan", "JP"], ["filipino", "PH"], ["philippines?", "PH"], ["swedish", "SE"], ["sweden", "SE"],
  ["danish", "DK"], ["denmark", "DK"], ["norwegian", "NO"], ["norway", "NO"], ["greek", "GR"], ["greece", "GR"],
  ["persian", "IR"], ["iran\\w*", "IR"], ["thai", "TH"], ["vietnamese", "VN"], ["african?", UNKNOWN_COUNTRY],
  ["nollywood", "NG"], ["nigeria\\w*", "NG"], ["australia\\w*", "AU"], ["canada", "CA"], ["canadian", "CA"],
  ["caribbean", UNKNOWN_COUNTRY], ["jamaica\\w*", "JM"], ["mexico", "MX"], ["mexican", "MX"], ["irish", "IE"],
  ["ireland", "IE"], ["scottish", "GB"], ["scotland", "GB"], ["british", "GB"], ["united kingdom", "GB"],
  ["saudi", "SA"], ["kurdish", UNKNOWN_COUNTRY], ["albanian?", "AL"], ["romanian?", "RO"], ["hungarian?", "HU"],
  ["czech", "CZ"], ["ukrain\\w*", "UA"], ["israel\\w*", "IL"], ["hebrew", "IL"], ["afghan\\w*", "AF"], ["somali\\w*", "SO"],
];
const WORD_PATTERNS = WORDS.map(([pattern, country]) => [new RegExp(`^(${pattern})$`), country] as const);
const ANY_WORD = new RegExp(`\\b(${WORDS.map(([pattern]) => pattern).join("|")})\\b`);

/** USA however the provider spells it: "USA", "US| ...", "[US]", "United States". */
const USA = /^(usa|us|united states)\b|\busa\b/;

/** The country a name is tagged with ("US", "GB", ... or UNKNOWN_COUNTRY), or null when it names none. */
export function countryOfName(name: string): string | null {
  const words = name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  if (USA.test(words)) return "US";
  const [first = "", ...rest] = words.split(" ");
  if (rest.length > 0) {
    if (/^[a-z]{2}$/.test(first) && !HOME_OR_WORD.has(first)) return TWO_LETTER[first] ?? first.toUpperCase();
    const three = THREE_LETTER[first];
    if (three) return three;
  }
  const word = ANY_WORD.exec(words)?.[1];
  if (!word) return null;
  return WORD_PATTERNS.find(([pattern]) => pattern.test(word))?.[1] ?? UNKNOWN_COUNTRY;
}

/** Tagged with a country that is not the viewer's (a name with no country is nobody's "other"). */
export function isOtherCountry(name: string, viewer = "US"): boolean {
  const country = countryOfName(name);
  return country !== null && country !== viewer;
}

/** Tagged with the viewer's own country. */
export function isViewerCountry(name: string, viewer: string): boolean {
  return countryOfName(name) === viewer;
}
